import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useRole } from '../context/RoleContext';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useT, useFormatDate } from '../i18n';

type AlertTab = 'open' | 'past' | 'mute-requests';

interface Alert {
  id: number;
  kind: string;
  participant_id: number;
  measure_id: number | null;
  opened_at: string;
  last_seen_at: string;
  resolved_at: string | null;
  resolved_reason: string | null;
  detail: any;
  participant_first_name: string | null;
  participant_last_name: string | null;
  measure_type_code: string | null;
  measure_label: string | null;
  measure_contract_number: string | null;
}

interface MuteRequest {
  id: number;
  alert_id: number | null;
  kind: string;
  measure_id: number;
  participant_id: number;
  reason: string | null;
  requested_by: number;
  requested_at: string;
  status: 'pending' | 'approved' | 'denied';
  decided_by: number | null;
  decided_at: string | null;
  decision_note: string | null;
  requested_by_name: string | null;
  decided_by_name: string | null;
  participant_first_name: string | null;
  participant_last_name: string | null;
  measure_type_code: string | null;
  measure_label: string | null;
}

type Fd = ReturnType<typeof useFormatDate>;
type T = ReturnType<typeof useT>['t'];

function fmtDate(iso: string | null, fd: Fd): string {
  if (!iso) return '—';
  return fd.short(iso);
}

function fmtRelative(iso: string, t: T, fd: Fd): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return t('notifs.minutesAgo', { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('notifs.hoursAgo', { n: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return t('notifs.daysAgo', { n: days });
  return fmtDate(iso, fd);
}

function KindLabel({ kind, t }: { kind: string; t: T }) {
  const label = kind === 'journal_stale' ? t('notifs.kindJournalStale') : kind;
  return <span className="badge badge-sm">{label}</span>;
}

function ResolvedReason({ reason, t }: { reason: string | null; t: T }) {
  if (!reason) return null;
  const map: Record<string, string> = {
    journal_added: t('notifs.reasonJournalAdded'),
    mesure_ended: t('notifs.reasonMesureEnded'),
    muted_approved: t('notifs.reasonMutedApproved'),
  };
  return <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>{map[reason] ?? reason}</span>;
}

export function NotificationsPage() {
  const { t } = useT();
  const fd = useFormatDate();
  const { role, isResponsable, isAdmin } = useRole();
  const canDecide = isAdmin || isResponsable;

  const initialTab: AlertTab = (() => {
    const m = window.location.hash.match(/tab=([\w-]+)/);
    if (m && (m[1] === 'open' || m[1] === 'past' || m[1] === 'mute-requests')) return m[1] as AlertTab;
    return 'open';
  })();
  const [tab, setTab] = useState<AlertTab>(initialTab);

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [counts, setCounts] = useState<{ open: number; past: number; total: number }>({ open: 0, past: 0, total: 0 });
  const [muteRequests, setMuteRequests] = useState<MuteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);

  // Mute-request prompt state
  const [muteFor, setMuteFor] = useState<Alert | null>(null);
  const [muteReason, setMuteReason] = useState('');

  // Decision prompt
  const [decision, setDecision] = useState<{ kind: 'approve' | 'deny'; req: MuteRequest } | null>(null);
  const [decisionNote, setDecisionNote] = useState('');

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'mute-requests') {
        const data = await api.getMuteRequests() as any;
        setMuteRequests(data?.mute_requests ?? []);
      } else {
        const data = await api.getAlerts({ status: tab, limit: 500 }) as any;
        setAlerts(data?.alerts ?? []);
        setCounts(data?.counts ?? { open: 0, past: 0, total: 0 });
      }
    } catch (e: any) {
      setError(e?.message || t('notifs.errLoad'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Also load counts even when on mute-requests tab so badges stay accurate.
  useEffect(() => {
    if (tab === 'mute-requests') {
      (api.getAlerts({ status: 'all', limit: 1 }) as Promise<any>)
        .then((data) => setCounts(data?.counts ?? { open: 0, past: 0, total: 0 }))
        .catch(() => { /* ignore */ });
    }
  }, [tab]);

  async function submitMuteRequest() {
    if (!muteFor) return;
    setActingId(muteFor.id);
    try {
      await api.requestAlertMute(muteFor.id, muteReason);
      setMuteFor(null);
      setMuteReason('');
      await reload();
    } catch (e: any) {
      setError(e?.message || t('notifs.errMuteRequest'));
    } finally {
      setActingId(null);
    }
  }

  async function submitDecision() {
    if (!decision) return;
    setActingId(decision.req.id);
    try {
      if (decision.kind === 'approve') await api.approveMuteRequest(decision.req.id, decisionNote);
      else await api.denyMuteRequest(decision.req.id, decisionNote);
      setDecision(null);
      setDecisionNote('');
      await reload();
    } catch (e: any) {
      setError(e?.message || t('notifs.errDecision'));
    } finally {
      setActingId(null);
    }
  }

  const pendingMutes = muteRequests.filter((r) => r.status === 'pending').length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s12)', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 'var(--t-17)', fontWeight: 700 }}>{t('notifs.pageTitle')}</h1>
        <span className="stat-pill" title={t('notifs.activeTooltip')}>{t('notifs.activeCount', { n: counts.open })}</span>
        <span className="stat-pill" title={t('notifs.archivedTooltip')}>{t('notifs.archivedCount', { n: counts.past })}</span>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'inline-flex', borderRadius: 'var(--r1)', overflow: 'hidden',
        border: '1px solid var(--line)', marginBottom: 'var(--s12)',
      }}>
        {(['open', 'past', ...(canDecide || role === 'utilisateur' ? ['mute-requests'] : [])] as AlertTab[]).map(tb => (
          <button
            key={tb}
            type="button"
            onClick={() => setTab(tb)}
            style={{
              padding: 'var(--s4) var(--s12)',
              background: tab === tb ? 'var(--accent)' : 'var(--surface)',
              color: tab === tb ? 'var(--surface)' : 'var(--ink-2)',
              border: 'none',
              cursor: 'pointer',
              fontSize: 'var(--t-12)',
              fontWeight: 600,
            }}
          >
            {tb === 'open' ? t('notifs.tabOpen') : tb === 'past' ? t('notifs.tabPast') : t('notifs.tabMuteRequests')}
            {tb === 'mute-requests' && pendingMutes > 0 && (
              <span style={{
                marginLeft: 6,
                padding: '0 5px',
                minWidth: 18,
                height: 18,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: tab === tb ? 'var(--surface)' : 'var(--accent)',
                color: tab === tb ? 'var(--accent)' : 'var(--surface)',
                borderRadius: 9,
                fontSize: 10,
                fontWeight: 700,
              }}>{pendingMutes}</span>
            )}
          </button>
        ))}
      </div>

      {error && <div style={{ color: 'var(--err)', marginBottom: 'var(--s8)' }}>{error}</div>}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s32)' }}><div className="spinner" /></div>
      ) : tab === 'mute-requests' ? (
        <MuteRequestsTable
          requests={muteRequests}
          canDecide={canDecide}
          onApprove={(req) => { setDecisionNote(''); setDecision({ kind: 'approve', req }); }}
          onDeny={(req) => { setDecisionNote(''); setDecision({ kind: 'deny', req }); }}
        />
      ) : (
        <AlertsTable
          alerts={alerts}
          isPast={tab === 'past'}
          onMute={(a) => { setMuteReason(''); setMuteFor(a); }}
          actingId={actingId}
        />
      )}

      {/* Mute request prompt */}
      {muteFor && (
        <div
          onClick={() => setMuteFor(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1100, padding: 'var(--s16)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            style={{
              background: 'var(--surface)',
              width: 'min(520px, 100%)',
              borderRadius: 'var(--r1)',
              boxShadow: '0 10px 32px rgba(0,0,0,0.30)',
              padding: 'var(--s16)',
            }}
          >
            <h2 style={{ marginTop: 0, fontSize: 'var(--t-15)', fontWeight: 700 }}>{t('notifs.mutePromptTitle')}</h2>
            <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-13)' }}>
              {t('notifs.mutePromptBody', { measure: muteFor.measure_label ?? '' })}
              <strong>{muteFor.participant_first_name} {muteFor.participant_last_name}</strong>.
            </p>
            <label style={{ display: 'block', marginBottom: 'var(--s4)', fontSize: 'var(--t-12)', fontWeight: 600 }}>
              {t('notifs.reasonRequiredLabel')}
            </label>
            <textarea
              value={muteReason}
              onChange={(e) => setMuteReason(e.target.value)}
              rows={4}
              autoFocus
              placeholder={t('notifs.reasonPlaceholder')}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: 'var(--s8)',
                borderRadius: 'var(--r2)',
                border: '1px solid var(--line)',
                fontSize: 'var(--t-12)',
                resize: 'vertical',
              }}
            />
            <div style={{ marginTop: 'var(--s12)', display: 'flex', gap: 'var(--s8)', justifyContent: 'flex-end' }}>
              <button type="button" className="sort-btn" onClick={() => setMuteFor(null)}>{t('common.cancel')}</button>
              <button
                type="button"
                disabled={!muteReason.trim() || actingId === muteFor.id}
                onClick={submitMuteRequest}
                style={{
                  padding: 'var(--s4) var(--s12)',
                  borderRadius: 'var(--r2)',
                  border: 'none',
                  background: 'var(--accent)',
                  color: 'var(--surface)',
                  fontSize: 'var(--t-12)',
                  fontWeight: 600,
                  cursor: actingId === muteFor.id ? 'wait' : 'pointer',
                  opacity: !muteReason.trim() ? 0.5 : 1,
                }}
              >
                {t('notifs.sendRequest')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decision prompt */}
      <ConfirmDialog
        open={decision != null}
        title={decision?.kind === 'approve' ? t('notifs.approveTitle') : t('notifs.denyTitle')}
        message={
          decision?.kind === 'approve'
            ? t('notifs.approveMsg', {
                kind: decision.req.kind,
                measure: decision.req.measure_label ?? '',
                name: `${decision.req.participant_first_name ?? ''} ${decision.req.participant_last_name ?? ''}`.trim(),
              })
            : t('notifs.denyMsg', { name: decision?.req?.requested_by_name ?? t('notifs.defaultRequester') })
        }
        confirmLabel={decision?.kind === 'approve' ? t('notifs.approveLabel') : t('notifs.denyLabel')}
        tone={decision?.kind === 'approve' ? 'ok' : 'danger'}
        busy={actingId != null}
        onConfirm={submitDecision}
        onCancel={() => setDecision(null)}
      />
    </div>
  );
}

function AlertsTable({
  alerts,
  isPast,
  onMute,
  actingId,
}: {
  alerts: Alert[];
  isPast: boolean;
  onMute: (a: Alert) => void;
  actingId: number | null;
}) {
  const { t } = useT();
  const fd = useFormatDate();
  if (alerts.length === 0) {
    return (
      <div style={{ padding: 'var(--s24)', textAlign: 'center', color: 'var(--ink-3)' }}>
        {isPast ? t('notifs.emptyPast') : t('notifs.emptyOpen')}
      </div>
    );
  }
  return (
    <table className="table table-compact" style={{ tableLayout: 'auto' }}>
      <thead>
        <tr>
          <th>{t('notifs.colType')}</th>
          <th>{t('notifs.colParticipant')}</th>
          <th>{t('notifs.colMeasure')}</th>
          <th>{t('notifs.colDetail')}</th>
          <th>{isPast ? t('notifs.colClosed') : t('notifs.colOpenSince')}</th>
          <th style={{ width: 1 }}></th>
        </tr>
      </thead>
      <tbody>
        {alerts.map((a, i) => (
          <tr key={a.id} style={{ backgroundColor: i % 2 === 1 ? 'var(--bg-inset)' : 'var(--surface)' }}>
            <td><KindLabel kind={a.kind} t={t} /></td>
            <td>
              <a href={`#participant?id=${a.participant_id}`} style={{ color: 'inherit', textDecoration: 'none', fontWeight: 500 }}>
                {a.participant_last_name} {a.participant_first_name}
              </a>
            </td>
            <td style={{ fontSize: 'var(--t-11)', color: 'var(--ink-2)' }}>
              {a.measure_label || '—'}
              {a.measure_type_code && (
                <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>· {a.measure_type_code}</span>
              )}
            </td>
            <td style={{ fontSize: 'var(--t-12)' }}>
              {a.kind === 'journal_stale' && a.detail ? (
                <>
                  {t('notifs.daysSinceNoJournal', { n: a.detail.days_since })}
                  {a.detail.last_entry_date && (
                    <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>
                      {t('notifs.lastEntryDate', { date: fmtDate(a.detail.last_entry_date, fd) })}
                    </span>
                  )}
                </>
              ) : (
                <span style={{ color: 'var(--ink-3)' }}>—</span>
              )}
            </td>
            <td style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
              {isPast ? (
                <>
                  {fmtDate(a.resolved_at, fd)}
                  <div><ResolvedReason reason={a.resolved_reason} t={t} /></div>
                </>
              ) : (
                fmtRelative(a.opened_at, t, fd)
              )}
            </td>
            <td style={{ whiteSpace: 'nowrap' }}>
              {!isPast && (
                <button
                  type="button"
                  className="sort-btn"
                  disabled={actingId === a.id}
                  onClick={() => onMute(a)}
                  title={t('notifs.muteTooltip')}
                >
                  🔕 {t('notifs.muteButton')}
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MuteRequestsTable({
  requests,
  canDecide,
  onApprove,
  onDeny,
}: {
  requests: MuteRequest[];
  canDecide: boolean;
  onApprove: (r: MuteRequest) => void;
  onDeny: (r: MuteRequest) => void;
}) {
  const { t } = useT();
  const fd = useFormatDate();
  if (requests.length === 0) {
    return (
      <div style={{ padding: 'var(--s24)', textAlign: 'center', color: 'var(--ink-3)' }}>
        {t('notifs.emptyMuteRequests')}
      </div>
    );
  }
  return (
    <table className="table table-compact" style={{ tableLayout: 'auto' }}>
      <thead>
        <tr>
          <th>{t('notifs.colStatus')}</th>
          <th>{t('notifs.colParticipant')}</th>
          <th>{t('notifs.colMeasure')}</th>
          <th>{t('notifs.colRequester')}</th>
          <th>{t('notifs.colReason')}</th>
          <th>{t('notifs.colRequested')}</th>
          {canDecide && <th style={{ width: 1 }}></th>}
        </tr>
      </thead>
      <tbody>
        {requests.map((r, i) => (
          <tr key={r.id} style={{ backgroundColor: i % 2 === 1 ? 'var(--bg-inset)' : 'var(--surface)' }}>
            <td>
              {r.status === 'pending' && <span className="badge badge-sm" style={{ background: 'var(--warn)', color: 'var(--surface)' }}>{t('notifs.statusPending')}</span>}
              {r.status === 'approved' && <span className="badge badge-sm badge-ok">{t('notifs.statusApproved')}</span>}
              {r.status === 'denied' && <span className="badge badge-sm" style={{ background: 'var(--err)', color: 'var(--surface)' }}>{t('notifs.statusDenied')}</span>}
            </td>
            <td style={{ fontWeight: 500 }}>
              <a href={`#participant?id=${r.participant_id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                {r.participant_last_name} {r.participant_first_name}
              </a>
            </td>
            <td style={{ fontSize: 'var(--t-11)', color: 'var(--ink-2)' }}>
              {r.measure_label || '—'}
              {r.measure_type_code && <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>· {r.measure_type_code}</span>}
            </td>
            <td style={{ fontSize: 'var(--t-12)' }}>{r.requested_by_name || '—'}</td>
            <td style={{ fontSize: 'var(--t-12)', maxWidth: 320 }}>{r.reason || <span style={{ color: 'var(--ink-3)' }}>{t('notifs.noneParenthesized')}</span>}</td>
            <td style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{fmtRelative(r.requested_at, t, fd)}</td>
            {canDecide && (
              <td style={{ whiteSpace: 'nowrap' }}>
                {r.status === 'pending' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => onApprove(r)}
                      style={{
                        padding: 'var(--s4) var(--s8)',
                        borderRadius: 'var(--r2)',
                        border: 'none',
                        background: 'var(--ok)',
                        color: 'var(--surface)',
                        fontSize: 'var(--t-11)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        marginRight: 'var(--s4)',
                      }}
                    >
                      {t('notifs.approveLabel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeny(r)}
                      style={{
                        padding: 'var(--s4) var(--s8)',
                        borderRadius: 'var(--r2)',
                        border: '1px solid var(--line)',
                        background: 'var(--surface)',
                        color: 'var(--err)',
                        fontSize: 'var(--t-11)',
                        cursor: 'pointer',
                      }}
                    >
                      {t('notifs.denyLabel')}
                    </button>
                  </>
                ) : r.decided_by_name ? (
                  <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
                    {t('notifs.decidedBy', { name: r.decided_by_name })}
                  </span>
                ) : null}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
